import type { Mutable, RunnerItem } from '../utils';
import { Runner, Signals } from '../utils';
import { SmoothTime } from '../utils/SmoothTime';
import { Time } from '../utils/Time';
import { PoolGroup, odieDestroySystem } from '.';
import type { Entity } from './Entity';
import { Group } from './groups/Group';
import { generateQueries, QueryManager } from './queries';
import type { System, SystemClass } from './System';

export interface SceneOptions {
  updateAndRender?: boolean;
  autoStart?: boolean;
  smoothTime?: number;
}

/**
 * Game, dd does exactly what it says on the tin! A game is essentially:
 *
 * 1 - A bunch of game Entities
 *
 * 2 - a collection of Systems all interacting with each other and the entities
 *
 * This game is the most minimal version of ODIE, check out BasicGame for something a little higher level
 *
 * Key events in Odie are powered by Runner's
 * the great thing is entities and components automatically get attached to them, so as
 * long as the correct function exists in a system / component it will be called.
 *
 * ```
 *
 * const game = new Game();
 *
 * game.addSystem(SpawnSystem, {maxEnemies:10});
 *
 * game.spawn.addEnemy();
 *
 * requestAnimationFrame(dt)
 * {
 *    game.update(dt);
 * }
 * ```
 */
export class Scene extends Signals {
  /** A list of ALL systems currently in the scene */
  public readonly allSystems: Map<string, System> = new Map();

  /** A list of ALL entities currently in the scene */
  public readonly allEntities: Group<Entity> = new Group();

  /** handles time calculations for each frame */
  public readonly time: Time;

  /** manages the queries for systems */
  public readonly queryManager: QueryManager = new QueryManager(this);

  /** whether or not the scene is paused, updates will pause when true */
  public paused = false;

  /** all runners that a system will be assigned to if they implement the function */
  public readonly runners = {
    /** A runner called when the scene is resized. Calls `resize()` on any registered objects. */
    resize: new Runner('resize', 2),
    /** A runner called before a scene update happens. Calls `preupdate()` on any registered objects. */
    preupdate: new Runner('preupdate', 1),
    /** A runner called before a scene update happens. Calls `update()` on any registered objects. */
    update: new Runner('update', 1),
    /** A runner called after the scene has updated. Calls `postupdate()` on any registered objects. */
    postupdate: new Runner('postupdate', 1),
    /** A runner called before a scene render happens. Calls `prerender()` on any registered objects. */
    prerender: new Runner('prerender', 1),
    /** A runner called before a scene render happens. Calls `render()` on any registered objects. */
    render: new Runner('render', 1),
    /** A runner called after the scene has render. Calls `postrender()` on any registered objects. */
    postrender: new Runner('postrender', 1),
    /** A runner called when a scene is about to be emptied. Calls `reset()` on any registered objects. */
    reset: new Runner('reset', 1),
  };

  /**
   * whether or not the scene is enabled, updates will pause when false.
   * scenes are enabled through `scene.start` and disabled with `scene.reset`
   */
  private _enabled = false;

  private _width = 100;
  private _height = 100;
  private _entitiesToRemove: Entity[] = [];

  /** If true update will be called automatically before a scene is rendered. defaults to true */
  private readonly _updateAndRender: boolean;

  /** systems to call start on before first update */
  private readonly _systemsToStart: System[] = [];

  /**
   * @param data - Options to use for your game
   * updateAndRender - True if you want the update and render to be
   * called simultaneously. This can be handy if you want to render and update the game at different rates
   * smoothTime - If is set, the scene will use SmoothTime instead of Time, that can potentially
   * help to prevent some small hiccups caused by frame rate oscilation.
   */
  constructor(data: SceneOptions = {}) {
    super();

    this.time = data.smoothTime
      ? new SmoothTime({ smooth: data.smoothTime })
      : new Time();
    this._updateAndRender = data.updateAndRender ?? true;
    data.autoStart = data.autoStart ?? false;
    data.autoStart && this.start();
  }

  public start() {
    if (this._enabled) return;

    this.allSystems.forEach((system) => {
      system.awake?.();
    });

    this._enabled = true;
  }

  /**
   * Adds a system to the scene, passing data to the `init` function of the system if implemented.
   *
   * @param Class - A class to create an instance of.
   * @param data - A list of options passed to the System when it is initialized.
   */
  public addSystem<S extends System>(
    Class: SystemClass<any, Scene, S>,
    data?: S['init'] extends (...args: any) => any
      ? Parameters<S['init']>[0]
      : undefined,
  ): S {
    const name = Class.NAME;

    if (!name)
      throw new Error('[SystemManager]: cannot add System without name');

    if (this.allSystems.has(name)) {
      return this.allSystems.get(name) as S;
    }

    const system = new Class() as Mutable<S>;

    system.scene = this;
    system.queries = {};
    generateQueries(Class as SystemClass, system);

    system.init?.(data);
    this._enabled && system.awake?.();

    const { runners } = this;

    for (const key in runners)
      runners[key as keyof Scene['runners']].add(
        system as RunnerItem<keyof Scene['runners']>,
      );

    this._systemsToStart.push(system);
    this.allSystems.set(Class.NAME, system);

    return system;
  }

  /**
   * return the system of the specified type
   * @param Class - instance of class to get
   */
  public getSystem<S extends System>(Class: SystemClass<any, Scene, S>): S {
    return this.allSystems.get(Class.NAME) as S;
  }

  /**
   * Directly adds an entity to the game.
   * @param entity - the entity to add to the game
   */
  public addToScene(entity: Entity): void {
    if (entity._gc) {
      const index = this._entitiesToRemove.indexOf(entity);

      this._entitiesToRemove.splice(index, 1);

      entity._gc = false;
    }

    if (entity.scene) {
      if (entity.scene === this) {
        return;
      }

      this._removeEntity(entity);
    }

    entity.scene = this;

    this.allEntities.add(entity);
    this.queryManager.onEntityAdded(entity);
  }

  /**
   * Directly removes an entity from the game
   * @param entity - the entity to remove
   */
  public removeFromScene(entity: Entity): void {
    if (entity._gc || !entity.scene) return;

    entity._gc = true;
    this._entitiesToRemove.push(entity);
  }

  /** Called before the game is rendered. Called by a render system before it is about to draw to the screen */
  public renderStart(): void {
    if (this._updateAndRender) {
      this.update();
    }

    this.runners.prerender.run(this.time);
    this.runners.render.run(this.time);
  }

  /** called by a render system when it has finished rendering */
  public renderFinish() {
    this.runners.postrender.run(this.time);
  }

  /** Called automatically by View2DSystem/View3DSystem before each render if `updateAndRender` is true. Otherwise you must call this manually */
  public update(): void {
    if (this.paused || !this._enabled) return;

    this.time.nextUpdate();

    this._startSystems();
    this._removeEntities();

    this.runners.preupdate.run(this.time);

    this._removeEntities();

    this.runners.update.run(this.time);

    this._removeEntities();

    this.runners.postupdate.run(this.time);
  }

  /** Restarts the scene by calling `scene.reset` and `scene.start` */
  public restart() {
    this.reset();
    this.start();
  }

  /**
   * This is called when you want to essentially clean out the game. Its like wiping the slate clean
   * All systems will be notified and all entities will be removed.
   */
  public reset(): void {
    this._removeEntities();

    this.runners.reset.run();

    // The FIRST item is the System..
    for (let i = this.allEntities.children.length - 1; i >= 0; --i) {
      const entity = this.allEntities.children[i];

      entity._released = true;

      this._removeEntity(entity);
    }

    this.allEntities.empty();

    for (const key in this.runners) {
      this.runners[key as keyof Scene['runners']].destroy();
    }

    this._entitiesToRemove.length = 0;

    // add systems back
    this.allSystems.forEach((system) => {
      for (const rKey in this.runners) {
        this.runners[rKey as keyof Scene['runners']].add(
          system as RunnerItem<keyof Scene['runners']>,
        );
      }

      this._systemsToStart.push(system);
    });

    this._enabled = false;
  }

  /**
   * Calls resize on all systems subscribed to the resize runner
   * @param w - the width to make the game
   * @param h - the height to make the game
   */
  public resize(w: number, h: number): void {
    this._width = w;
    this._height = h;
    this.runners.resize.run(w, h);
  }

  /** removes all entities that need to be removed from the scene */
  private _removeEntities(): void {
    const entitiesToRemove = this._entitiesToRemove;

    if (!entitiesToRemove.length) return;

    for (let i = 0; i < entitiesToRemove.length; i++) {
      const entity = entitiesToRemove[i];

      this._removeEntity(entity);
    }

    entitiesToRemove.length = 0;
  }

  /** removes an entity from the scene */
  private _removeEntity(entity: Entity): void {
    if (entity._released) {
      entity._pooled ? PoolGroup.return(entity) : entity.reset?.();
    }
    this.queryManager.onEntityRemoved(entity);
    this.allEntities.remove(entity);
    entity._gc = false;
    entity.scene = undefined;
    entity._released = false;
  }

  /** calls the start function on systems */
  private _startSystems(): void {
    for (let i = 0; i < this._systemsToStart.length; i++) {
      const system = this._systemsToStart[i];

      system.start?.();
    }

    this._systemsToStart.length = 0;
  }

  /** the width of the game. to modify this its best to call `game.resize(w, h)` */
  public get width(): number {
    return this._width;
  }

  /** the height of the game. to modify this its best to call `game.resize(w, h)` */
  public get height(): number {
    return this._height;
  }

  /** Destroys the scene */
  public destroy() {
    this.reset();

    // Loop through all entities and destroy them
    for (const entity of this.allEntities.children) {
      entity.destroy();
    }

    // Loop through all entities and destroy them
    for (const system of this.allSystems.values()) {
      odieDestroySystem(system);
    }

    // Clear everything
    this.allSystems.clear();
    this.allEntities.empty();
    this.queryManager.destroy();
    this.runners.resize.destroy();
    this.runners.preupdate.destroy();
    this.runners.update.destroy();
    this.runners.postupdate.destroy();
    this.runners.prerender.destroy();
    this.runners.render.destroy();
    this.runners.postrender.destroy();
    this.runners.reset.destroy();
  }
}
